import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { MessageCircle, Plus, X, Edit2, Trash2, MoreVertical } from "lucide-react";
import logo1 from "../../assets/Image (3).jpg";
import "./ChatHistoryMenu.css";

const ChatSidebar = ({
  sidebarOpen,
  setSidebarOpen,
  chats,
  activeChat,
  setActiveChat,
  createNewChat,
  openMenuId,
  setOpenMenuId,
  renamingChatId,
  setRenamingChatId,
  renameValue,
  setRenameValue,
  handleRenameSubmit,
  handleRenameCancel,
  handleDeleteClick,
  toggleMenu,
  handleRenameClick,
}) => {
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setOpenMenuId]);

  return (
    <div
      className={`${
        sidebarOpen ? "w-64" : "w-0"
      } transition-all duration-300 bg-white text-gray-800 overflow-hidden flex flex-col`}
      style={{ height: "calc(100vh - 140px)" }}
    >
      <div className="p-4 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900">
            <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-white border">
              <img
                src={logo1}
                alt="AI Logo"
                className="w-full h-full object-contain"
              />
            </div>
            BlueGecko AI
          </h2>

          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* New Chat Button */}
        <button
          onClick={createNewChat}
          className="w-full flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors mb-4"
        >
          <Plus className="w-4 h-4" />
          New Chat
        </button>

        {/* Chats List */}
        <div className="space-y-1 flex-1 overflow-y-auto">
          {chats.map((chat) => (
            <button
              key={chat.id}
              type="button"
              className={`chat-history-item ${
                activeChat === chat.id ? "active" : ""
              }`}
              onClick={() => setActiveChat(chat.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActiveChat(chat.id);
                }
              }}
              aria-label={`Select chat: ${chat.title}`}
            >
              <div className="chat-history-content">
                <MessageCircle className="chat-history-icon" />
                {renamingChatId === chat.id ? (
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleRenameSubmit(chat.id);
                      } else if (e.key === "Escape") {
                        handleRenameCancel();
                      }
                    }}
                    onBlur={() => handleRenameSubmit(chat.id)}
                    className="chat-rename-input"
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="chat-history-title">{chat.title}</span>
                )}
              </div>

              <div
                className="chat-menu-container"
                ref={openMenuId === chat.id ? menuRef : null}
              >
                <button
                  onClick={(e) => toggleMenu(chat.id, e)}
                  className="chat-menu-trigger"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>

                {openMenuId === chat.id && (
                  <div className="chat-menu-dropdown">
                    <button
                      onClick={(e) =>
                        handleRenameClick(chat.id, chat.title, e)
                      }
                      className="chat-menu-item"
                    >
                      <Edit2 className="w-4 h-4" />
                      <span>Rename</span>
                    </button>
                    <button
                      onClick={(e) => handleDeleteClick(chat.id, e)}
                      className="chat-menu-item delete"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

ChatSidebar.propTypes = {
  sidebarOpen: PropTypes.bool.isRequired,
  setSidebarOpen: PropTypes.func.isRequired,
  chats: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeChat: PropTypes.string,
  setActiveChat: PropTypes.func.isRequired,
  createNewChat: PropTypes.func.isRequired,
  openMenuId: PropTypes.string,
  setOpenMenuId: PropTypes.func.isRequired,
  renamingChatId: PropTypes.string,
  setRenamingChatId: PropTypes.func.isRequired,
  renameValue: PropTypes.string,
  setRenameValue: PropTypes.func.isRequired,
  handleRenameSubmit: PropTypes.func.isRequired,
  handleRenameCancel: PropTypes.func.isRequired,
  handleDeleteClick: PropTypes.func.isRequired,
  toggleMenu: PropTypes.func.isRequired,
  handleRenameClick: PropTypes.func.isRequired,
};

export default ChatSidebar;